/* eslint-disable no-nested-ternary */
import { ReportDataType } from '../reports/types'
import { BudgetingScenario } from '../../../features/scenario/types/BudgetingScenario'
import { SET_CONTEXT_COMPANY } from '../company/types'
import {
  FilterActionTypes,
  Filters,
  SET_DATATYPE_FILTER,
  SET_FISCAL_YEAR_FILTER,
  TOGGLE_DISPLAY_EMPTY_ROWS,
  TOGGLE_DATATYPE_FILTER,
  SET_BUDGETING_SCENARIO_FILTER,
  SET_CONTEXT_FILTER,
  SET_CUMULATIVE_FILTER,
  ADD_PERIOD_GROUP,
  REMOVE_PERIOD_GROUP,
  SET_PERIOD_GROUP_FILTER,
  TOGGLE_CUMULATIVE_FILTER,
  SET_PERIOD_GROUPS,
  SET_DIMENSIONS_FILTER,
  RESET_CONTEXT_FILTER,
  SET_DIMENSIONS_QUERY,
  SET_PERIOD_GROUP_SELECTION,
  SET_FISCAL_YEAR_IDS,
  SET_GROUP_SORT_ORDER,
  SET_MONTH_SORT_ORDER
} from './types'

const swapDataTypes = (dataTypes: ReportDataType[], scenario?: BudgetingScenario): ReportDataType[] => {
  const hadPlanType = dataTypes.includes(ReportDataType.budget) || dataTypes.includes(ReportDataType.forecast)
  if (!hadPlanType) return dataTypes

  const base: ReportDataType[] = dataTypes.filter(dt => dt !== ReportDataType.budget && dt !== ReportDataType.forecast)
  const canonical = scenario?.forecastCursor ? ReportDataType.forecast : ReportDataType.budget
  return base.concat(canonical)
}

const initialState: Filters = {
  fiscalYear: null,
  periodGroup: undefined,
  dataTypes: [ReportDataType.actuals, ReportDataType.budget],
  displayEmptyRows: false,
  cumulativeFilter: undefined,
  periodGroups: undefined,
  dimensions: [],
  dimensionsQuery: undefined,
  fiscalYearIdsByCompany: {},
  groupSortOrder: 'asc',
  monthSortOrder: 'asc'
}

const filtersReducer = (state = initialState, action: FilterActionTypes): Filters => {
  switch (action.type) {
    case SET_CONTEXT_COMPANY:
    case RESET_CONTEXT_FILTER:
      // Fiscal year selection is stored per company and sort order is a display preference -> keep both
      return {
        ...initialState,
        fiscalYearIdsByCompany: state.fiscalYearIdsByCompany ?? initialState.fiscalYearIdsByCompany,
        groupSortOrder: state.groupSortOrder ?? initialState.groupSortOrder,
        monthSortOrder: state.monthSortOrder ?? initialState.monthSortOrder
      }
    case SET_CONTEXT_FILTER: {
      const newState = { ...state, ...action.payload }
      if (action.payload.budgetingScenario !== undefined) {
        newState.dataTypes = swapDataTypes(state.dataTypes, action.payload.budgetingScenario)
      }
      return newState
    }
    case SET_FISCAL_YEAR_FILTER:
      return { ...state, fiscalYear: action.payload }
    case SET_CUMULATIVE_FILTER:
      return { ...state, cumulativeFilter: action.payload }
    case SET_DIMENSIONS_FILTER:
      return { ...state, dimensions: action.payload }
    case SET_DIMENSIONS_QUERY:
      return { ...state, dimensionsQuery: action.payload }
    case SET_BUDGETING_SCENARIO_FILTER:
      return { ...state, budgetingScenario: action.payload, dataTypes: swapDataTypes(state.dataTypes, action.payload) }
    case SET_DATATYPE_FILTER:
      return { ...state, dataTypes: action.payload }
    case TOGGLE_DATATYPE_FILTER:
      return {
        ...state,
        dataTypes: state.dataTypes.includes(action.payload)
          ? state.dataTypes.filter(d => d !== action.payload)
          : state.dataTypes.concat(action.payload)
      }
    case TOGGLE_DISPLAY_EMPTY_ROWS:
      return { ...state, displayEmptyRows: !state.displayEmptyRows }
    case SET_PERIOD_GROUPS:
      return { ...state, periodGroups: action.payload }
    case SET_PERIOD_GROUP_SELECTION:
      return { ...state, periodGroupSelection: action.payload }
    case SET_FISCAL_YEAR_IDS: {
      const { companyId, fiscalYearIds } = action.payload
      const fiscalYearIdsByCompany = { ...state.fiscalYearIdsByCompany }
      if (fiscalYearIds?.length) {
        fiscalYearIdsByCompany[companyId] = fiscalYearIds
      } else {
        delete fiscalYearIdsByCompany[companyId]
      }
      return { ...state, fiscalYearIdsByCompany }
    }
    case SET_GROUP_SORT_ORDER:
      return { ...state, groupSortOrder: action.payload }
    case SET_MONTH_SORT_ORDER:
      return { ...state, monthSortOrder: action.payload }

    case ADD_PERIOD_GROUP:
      return {
        ...state,
        periodGroups: [...(state?.periodGroups || []), action.payload]
      }
    case REMOVE_PERIOD_GROUP:
      return {
        ...state,
        periodGroups: state?.periodGroups?.filter(period => period.id !== action.payload.id)
      }
    case SET_PERIOD_GROUP_FILTER:
      return { ...state, periodGroup: action.payload }
    case TOGGLE_CUMULATIVE_FILTER:
      return {
        ...state,
        cumulativeFilter: !state.cumulativeFilter
          ? action.payload
          : action.payload.month === state.cumulativeFilter.month && action.payload.year === state.cumulativeFilter.year
          ? undefined
          : action.payload
      }
    default:
      return state
  }
}

export default filtersReducer
